import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PortalNav } from "@/components/PortalNav";
import { CustomerMap } from "@/components/CustomerMap";
import { useAuth } from "@/context/AuthContext";
import { EmailToggle } from "@/components/EmailToggle";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  UserPlus, Loader2, Users, MapPin, X, Copy, Check, ChevronRight, Search, Bell,
  UserCog, CheckCircle2, Upload, FileSpreadsheet, AlertTriangle, Pencil, Trash2, KeyRound,
  Tag, Building2,
} from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  company_name: "", contact_name: "", email: "", phone: "", account_number: "",
  sales_person: "", assigned_rep_id: "", address: "", city: "", state: "", zip_code: "",
};

const REP_PALETTE = ["#D4AF37", "#2563EB", "#DC2626", "#059669", "#7C3AED", "#EA580C", "#0891B2", "#DB2777"];
const UNASSIGNED_COLOR = "#94A3B8";

export default function CrmDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [customers, setCustomers] = useState([]);
  const [reps, setReps] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showReps, setShowReps] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [credential, setCredential] = useState(null);
  const [query, setQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [missingGpsOnly, setMissingGpsOnly] = useState(false);
  const [prospectsOnly, setProspectsOnly] = useState(false);
  const [mapFilter, setMapFilter] = useState("all");
  const [geocoding, setGeocoding] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [converting, setConverting] = useState(false);

  const load = async () => {
    try {
      const [cRes, fRes] = await Promise.all([
        api.get("/crm/customers", { params: { include_archived: showArchived } }),
        api.get("/crm/followups"),
      ]);
      setCustomers(cRes.data.customers);
      setFollowups(fRes.data.followups);
      if (isAdmin) {
        const rRes = await api.get("/crm/reps");
        setReps(rRes.data.reps);
      }
    } catch (e) {
      /* CRM dashboard load failed; UI shows empty state */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  const reconcileReps = async () => {
    try {
      const { data } = await api.post("/crm/customers/reconcile-reps");
      toast.success(`Linked ${data.matched} customer${data.matched === 1 ? "" : "s"} to their sales rep`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reconcile failed");
    }
  };

  const geocodeMissing = async () => {
    setGeocoding(true);
    try {
      const { data } = await api.post("/crm/customers/geocode-missing");
      if (data.total_missing === 0) toast.info("All customers with an address already have GPS");
      else toast.success(`Geocoded ${data.updated} of ${data.total_missing}${data.remaining ? ` — ${data.remaining} left, click again` : ""}${data.failed ? ` (${data.failed} not found)` : ""}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Geocode failed");
    } finally {
      setGeocoding(false);
    }
  };

  const located = customers.filter((c) => typeof c.lat === "number" && typeof c.lng === "number");
  const repIds = [...new Set(customers.map((c) => c.assigned_rep_id).filter(Boolean))];
  const repColor = (id) => (id ? REP_PALETTE[repIds.indexOf(id) % REP_PALETTE.length] : UNASSIGNED_COLOR);
  const points = located.map((c) => ({
    id: c.id, lat: c.lat, lng: c.lng, color: repColor(c.assigned_rep_id), repId: c.assigned_rep_id || null,
    approximate: !!c.gps_approximate,
    label: `<strong>${c.company_name}</strong><br/>${c.assigned_rep_name || c.sales_person || "Unassigned"}${c.gps_approximate ? "<br/><em>approx. location</em>" : ""}`,
  }));
  const visiblePoints = mapFilter === "all"
    ? points
    : mapFilter === "unassigned"
      ? points.filter((p) => !p.repId)
      : points.filter((p) => p.repId === mapFilter);
  const legend = repIds.map((id) => ({
    id, color: repColor(id),
    name: customers.find((c) => c.assigned_rep_id === id)?.assigned_rep_name || "Rep",
  }));
  const hasUnassignedOnMap = located.some((c) => !c.assigned_rep_id);
  const today = new Date().toISOString().slice(0, 10);
  const filtered = customers.filter((c) =>
    [c.company_name, c.contact_name, c.email, c.account_number, c.assigned_rep_name || c.sales_person, c.city, c.state]
      .filter(Boolean).join(" ").toLowerCase().includes(query.toLowerCase())
  ).filter((c) => !missingGpsOnly || !(typeof c.lat === "number" && typeof c.lng === "number"))
   .filter((c) => !prospectsOnly || c.is_prospect)
   .sort((a, b) => (a.company_name || "").localeCompare(b.company_name || ""));

  const prospectCount = customers.filter((c) => c.is_prospect).length;
  const customerCount = customers.length - prospectCount;
  const visibleProspectIds = filtered.filter((c) => c.is_prospect).map((c) => c.id);
  const allProspectsSelected = visibleProspectIds.length > 0 && visibleProspectIds.every((id) => selected.has(id));
  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => setSelected((s) => { const n = new Set(s); if (allProspectsSelected) visibleProspectIds.forEach((id) => n.delete(id)); else visibleProspectIds.forEach((id) => n.add(id)); return n; });
  const bulkConvert = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setConverting(true);
    try {
      const { data } = await api.post("/crm/customers/bulk-convert", { customer_ids: ids });
      toast.success(`Converted ${data.converted} prospect${data.converted === 1 ? "" : "s"} to customer${data.converted === 1 ? "" : "s"}${data.skipped ? ` (${data.skipped} skipped)` : ""}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Convert failed");
    } finally { setConverting(false); }
  };

  const markDone = async (callId) => {
    await api.post(`/crm/calls/${callId}/done`);
    setFollowups((f) => f.filter((x) => x.id !== callId));
    toast.success("Follow-up cleared");
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] md:pl-[var(--sb-w,16rem)]">
      <PortalNav />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-2">CRM Console</p>
              <h1 className="font-display font-extrabold text-4xl tracking-tight text-[#0F172A]">Customers</h1>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {isAdmin && <EmailToggle />}
              {isAdmin && (
                <button onClick={reconcileReps} data-testid="crm-reconcile-reps" className="flex items-center gap-2 border border-[#0F172A] text-[#0F172A] px-4 py-3 text-sm hover:bg-zinc-100 transition-colors">
                  <Users className="h-4 w-4" /> Match Reps
                </button>
              )}
              {isAdmin && (
                <button onClick={() => setShowImport(true)} data-testid="crm-import-customers" className="flex items-center gap-2 border border-[#0F172A] text-[#0F172A] px-4 py-3 text-sm hover:bg-zinc-100 transition-colors">
                  <Upload className="h-4 w-4" /> Import
                </button>
              )}
              {isAdmin && (
                <button onClick={() => setShowReps(true)} data-testid="crm-manage-reps" className="flex items-center gap-2 border border-[#0F172A] text-[#0F172A] px-4 py-3 text-sm hover:bg-zinc-100 transition-colors">
                  <UserCog className="h-4 w-4" /> Sales Reps
                </button>
              )}
              <button onClick={geocodeMissing} disabled={geocoding} data-testid="crm-geocode-missing" className="flex items-center gap-2 border border-[#0F172A] text-[#0F172A] px-4 py-3 text-sm hover:bg-zinc-100 transition-colors disabled:opacity-60">
                {geocoding ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />} Geocode GPS
              </button>
              <button onClick={() => setShowCreate(true)} data-testid="crm-add-customer" className="flex items-center gap-2 bg-[#0F172A] text-white px-5 py-3 font-medium hover:bg-[#1E293B] transition-colors">
                <UserPlus className="h-4 w-4" /> New Prospect
              </button>
            </div>
          </div>

          {/* Follow-ups due */}
          {followups.length > 0 && (
            <div className="mt-8 bg-[#D4AF37]/10 border border-[#D4AF37]/40 p-5" data-testid="followups-panel">
              <div className="flex items-center gap-2 mb-3">
                <Bell className="h-4 w-4 text-[#B8860B]" />
                <h2 className="font-display font-bold text-[#0F172A]">Follow-ups ({followups.length})</h2>
              </div>
              <div className="space-y-2">
                {followups.map((f) => {
                  const overdue = f.follow_up_date && f.follow_up_date.slice(0, 10) < today;
                  return (
                  <div key={f.id} data-testid={`followup-${f.id}`} className={`flex items-center justify-between border px-4 py-2.5 ${overdue ? "bg-[#EF4444]/10 border-[#EF4444]/40 border-l-4 border-l-[#EF4444]" : "bg-white border-zinc-200"}`}>
                    <button onClick={() => navigate(`/crm/customers/${f.customer_id}`)} className="text-left">
                      <span className="font-medium text-sm text-[#0F172A]">{f.company_name || "Customer"}</span>
                      <span className="text-xs text-zinc-500 ml-2">{f.outcome || f.notes}</span>
                    </button>
                    <div className="flex items-center gap-3">
                      {overdue && <span className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#EF4444] font-medium" data-testid={`followup-overdue-${f.id}`}><AlertTriangle className="h-3 w-3" /> Overdue</span>}
                      <span className={`text-xs font-mono ${overdue ? "text-[#EF4444]" : "text-[#B8860B]"}`}>{(f.follow_up_date || "").slice(0, 10)}</span>
                      <button onClick={() => markDone(f.id)} data-testid={`followup-done-${f.id}`} className="text-zinc-400 hover:text-[#10B981] transition-colors" title="Mark done">
                        <CheckCircle2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
            <Stat icon={Building2} label="Customers" value={customerCount} />
            <Stat icon={Tag} label="Prospects" value={prospectCount} onClick={() => setProspectsOnly(true)} />
            <Stat icon={Users} label="Total records" value={customers.length} />
            <Stat icon={MapPin} label="On the map" value={located.length} />
          </div>

          {/* Map */}
          <div className="mt-8">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#D4AF37]" /><h2 className="font-display font-bold text-[#0F172A]">Customer Map</h2></div>
              {(repIds.length > 0 || hasUnassignedOnMap) && (
                <select value={mapFilter} onChange={(e) => setMapFilter(e.target.value)} data-testid="crm-map-rep-filter" className="px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]">
                  <option value="all">All reps ({located.length})</option>
                  {legend.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  {hasUnassignedOnMap && <option value="unassigned">Unassigned</option>}
                </select>
              )}
            </div>
            <CustomerMap points={visiblePoints} height={360} onSelect={(p) => navigate(`/crm/customers/${p.id}`)} />
            {(legend.length > 0 || hasUnassignedOnMap) && (
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="map-legend">
                {legend.map((l) => (
                  <span key={l.id} className="flex items-center gap-1.5 text-xs text-zinc-600">
                    <span className="h-3 w-3 rounded-full" style={{ background: l.color }} /> {l.name}
                  </span>
                ))}
                {hasUnassignedOnMap && (
                  <span className="flex items-center gap-1.5 text-xs text-zinc-600">
                    <span className="h-3 w-3 rounded-full" style={{ background: UNASSIGNED_COLOR }} /> Unassigned
                  </span>
                )}
              </div>
            )}
            {located.length === 0 && <p className="mt-2 text-xs text-zinc-400">No customers have a location yet. Add an address or GPS coordinates in a customer's profile.</p>}
          </div>

          {/* List */}
          <div className="mt-10 bg-white border border-zinc-200">
            <div className="p-5 border-b border-zinc-200 flex items-center justify-between gap-4 flex-wrap">
              <h2 className="font-display font-bold text-[#0F172A]">Directory</h2>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 justify-end">
                {visibleProspectIds.length > 0 && (
                  <label className="flex items-center gap-2 text-xs text-[#2563EB] font-medium cursor-pointer">
                    <input type="checkbox" checked={allProspectsSelected} onChange={toggleSelectAll} data-testid="crm-select-all-prospects" className="accent-[#2563EB]" />
                    Select all prospects
                  </label>
                )}
                <label className="flex items-center gap-2 text-xs text-[#2563EB] font-medium cursor-pointer">
                  <input type="checkbox" checked={prospectsOnly} onChange={(e) => setProspectsOnly(e.target.checked)} data-testid="crm-prospects-only" className="accent-[#2563EB]" />
                  Prospects only
                </label>
                <label className="flex items-center gap-2 text-xs text-zinc-500 cursor-pointer">
                  <input type="checkbox" checked={missingGpsOnly} onChange={(e) => setMissingGpsOnly(e.target.checked)} data-testid="crm-missing-gps" className="accent-[#D4AF37]" />
                  Missing GPS only
                </label>
                <label className="flex items-center gap-2 text-xs text-zinc-500 cursor-pointer">
                  <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="crm-show-archived" className="accent-[#D4AF37]" />
                  Show archived
                </label>
                <div className="relative">
                  <Search className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="crm-search" placeholder="Search company, email, account #…" className="pl-9 pr-3 py-2 border border-zinc-300 text-sm w-64 outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent" />
                </div>
              </div>
            </div>
            {selected.size > 0 && (
              <div className="px-5 py-3 bg-[#2563EB]/10 border-b border-[#2563EB]/30 flex items-center justify-between" data-testid="bulk-convert-bar">
                <span className="text-sm text-[#1D4FD7] font-medium">{selected.size} prospect(s) selected</span>
                <div className="flex items-center gap-3">
                  <button onClick={() => setSelected(new Set())} className="text-xs text-zinc-500 hover:text-[#0F172A]">Clear</button>
                  <button onClick={bulkConvert} disabled={converting} data-testid="bulk-convert-btn" className="flex items-center gap-2 bg-[#2563EB] text-white px-4 py-2 text-sm font-medium hover:bg-[#1D4FD7] transition-colors disabled:opacity-60">
                    {converting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tag className="h-4 w-4" />} Convert to Customer{selected.size > 1 ? "s" : ""}
                  </button>
                </div>
              </div>
            )}
            {loading ? (
              <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0F172A]" /></div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center text-zinc-400 text-sm" data-testid="crm-no-customers">No customers found.</div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {filtered.map((c) => (
                  <div key={c.id} onClick={() => navigate(`/crm/customers/${c.id}`)} data-testid={`crm-customer-${c.id}`} className={`cursor-pointer w-full text-left px-5 py-4 flex items-center gap-3 justify-between transition-colors group ${c.is_prospect ? "bg-[#2563EB]/10 border-l-4 border-[#2563EB] hover:bg-[#2563EB]/15" : "hover:bg-zinc-50"}`}>
                    {c.is_prospect && (
                      <input type="checkbox" checked={selected.has(c.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggleSelect(c.id)} data-testid={`crm-select-${c.id}`} className="accent-[#2563EB] h-4 w-4 shrink-0" />
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-7 gap-2 flex-1 min-w-0">
                      <div className="min-w-0">
                        <div className="font-medium flex items-center gap-2 text-[#0F172A]">
                          {c.company_name}
                          {c.is_prospect && <span data-testid={`crm-prospect-${c.id}`} className="text-[10px] uppercase tracking-wide text-white bg-[#2563EB] px-1.5 py-0.5">Prospect</span>}
                          {c.archived && <span className="text-[10px] uppercase tracking-wide text-zinc-400 border border-zinc-300 px-1.5 py-0.5">Archived</span>}
                        </div>
                        <div className="text-xs text-zinc-400 font-mono truncate">{c.email}</div>
                      </div>
                      <div className="text-sm text-zinc-600"><span className="text-[11px] uppercase text-zinc-400 block">Contact</span>{c.contact_name || "—"}</div>
                      <div className="text-sm text-zinc-600" data-testid={`crm-customer-account-${c.id}`}><span className="text-[11px] uppercase text-zinc-400 block">Account #</span>{c.account_number || "—"}</div>
                      <div className="text-sm text-zinc-600"><span className="text-[11px] uppercase text-zinc-400 block">Sales Person</span>{c.assigned_rep_name || c.sales_person || "—"}</div>
                      <div className="text-sm text-zinc-600"><span className="text-[11px] uppercase text-zinc-400 block">Location</span>{[c.city, c.state].filter(Boolean).join(", ") || "—"}</div>
                      <div className="text-sm" data-testid={`crm-customer-gps-${c.id}`}>
                        <span className="text-[11px] uppercase text-zinc-400 block">GPS?</span>
                        {typeof c.lat === "number" && typeof c.lng === "number"
                          ? (c.gps_approximate
                              ? <span className="inline-flex items-center gap-1 font-medium text-[#D97706]"><span className="h-1.5 w-1.5 rounded-full bg-[#D97706]" /> Approx.</span>
                              : <span className="inline-flex items-center gap-1 font-medium text-[#10B981]"><span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> Yes</span>)
                          : <span className="inline-flex items-center gap-1 font-medium text-[#EF4444]"><span className="h-1.5 w-1.5 rounded-full bg-[#EF4444]" /> No</span>}
                      </div>
                      <div className="text-sm" data-testid={`crm-customer-equip-${c.id}`}>
                        <span className="text-[11px] uppercase text-zinc-400 block">Equipment</span>
                        {c.has_equipment
                          ? <span className="inline-flex items-center gap-1 font-medium text-[#10B981]"><span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> Yes</span>
                          : <span className="text-zinc-400">No</span>}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-300 group-hover:text-[#0F172A] transition-colors" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </main>

      {showCreate && <CreateCustomerModal isAdmin={isAdmin} reps={reps} onClose={() => setShowCreate(false)} onCreated={(cred) => { setShowCreate(false); setCredential(cred); load(); }} />}
      {showReps && isAdmin && <RepsModal reps={reps} onClose={() => setShowReps(false)} onChange={load} onCredential={setCredential} />}
      {showImport && isAdmin && <ImportModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />}
      {credential && <CredentialModal cred={credential} onClose={() => setCredential(null)} />}
    </div>
  );
}

function Stat({ icon: Icon, label, value, onClick }) {
  return (
    <div onClick={onClick} data-testid={`crm-stat-${label.toLowerCase().replace(/\s+/g, "-")}`} className={`bg-white p-6 ${onClick ? "cursor-pointer hover:bg-zinc-50 transition-colors" : ""}`}>
      <Icon className="h-5 w-5 text-[#D4AF37]" />
      <div className="mt-3 font-display font-extrabold text-3xl text-[#0F172A]">{value}</div>
      <div className="text-xs uppercase tracking-wide text-zinc-400 mt-1">{label}</div>
    </div>
  );
}

function CreateCustomerModal({ isAdmin, reps, onClose, onCreated }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.assigned_rep_id) delete payload.assigned_rep_id;
      const { data } = await api.post("/crm/customers", payload);
      toast.success("Prospect created");
      onCreated(data.credentials);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg); toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="crm-create-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-white border border-zinc-200 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between p-5 border-b border-zinc-200 bg-[#0F172A] text-white">
          <div className="flex items-center gap-2"><UserPlus className="h-4 w-4 text-[#D4AF37]" /><h3 className="font-display font-bold">New Prospect</h3></div>
          <button onClick={onClose} data-testid="crm-close-create"><X className="h-5 w-5 text-zinc-300 hover:text-white" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4" data-testid="crm-create-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label="Company Name" value={form.company_name} onChange={set("company_name")} testid="crm-cc-company" required />
            <F label="Contact Name" value={form.contact_name} onChange={set("contact_name")} testid="crm-cc-contact" />
            <F label="Email Address" type="email" value={form.email} onChange={set("email")} testid="crm-cc-email" />
            <F label="Phone Number" value={form.phone} onChange={set("phone")} testid="crm-cc-phone" />
            <F label="Account Number" value={form.account_number} onChange={set("account_number")} testid="crm-cc-account" />
            {isAdmin ? (
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Sales Rep</label>
                <select value={form.assigned_rep_id} onChange={set("assigned_rep_id")} data-testid="crm-cc-rep" className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]">
                  <option value="">Unassigned</option>
                  {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
            ) : (
              <F label="Sales Person" value={form.sales_person} onChange={set("sales_person")} testid="crm-cc-sales" />
            )}
            <F label="Zip" value={form.zip_code} onChange={set("zip_code")} testid="crm-cc-zip" />
          </div>
          <F label="Address" value={form.address} onChange={set("address")} testid="crm-cc-address" />
          <div className="grid grid-cols-2 gap-4">
            <F label="City" value={form.city} onChange={set("city")} testid="crm-cc-city" />
            <F label="State" value={form.state} onChange={set("state")} testid="crm-cc-state" />
          </div>
          {error && <p className="text-sm text-[#EF4444]" data-testid="crm-cc-error">{error}</p>}
          <button type="submit" disabled={saving} data-testid="crm-cc-submit" className="w-full flex items-center justify-center gap-2 bg-[#0F172A] text-white py-3 font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create prospect"}
          </button>
          <p className="text-xs text-zinc-400">Only Company Name is required. If you enter an email, a customer login is auto-generated.</p>
        </form>
      </motion.div>
    </div>
  );
}

function RepsModal({ reps, onClose, onChange, onCredential }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      const payload = { name: form.name, email: form.email };
      if (form.password.trim()) payload.password = form.password.trim();
      const { data } = await api.post("/crm/reps", payload);
      setForm({ name: "", email: "", password: "" });
      toast.success("Sales rep created");
      onChange();
      onCredential(data.credentials);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg); toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="crm-reps-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-white border border-zinc-200 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between p-5 border-b border-zinc-200 bg-[#0F172A] text-white">
          <div className="flex items-center gap-2"><UserCog className="h-4 w-4 text-[#D4AF37]" /><h3 className="font-display font-bold">Sales Reps</h3></div>
          <button onClick={onClose} data-testid="crm-close-reps"><X className="h-5 w-5 text-zinc-300 hover:text-white" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4 border-b border-zinc-200" data-testid="crm-rep-form">
          <F label="Rep Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} testid="crm-rep-name" required />
          <F label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} testid="crm-rep-email" required />
          <F label="Password (optional)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testid="crm-rep-password" />
          {error && <p className="text-sm text-[#EF4444]" data-testid="crm-rep-error">{error}</p>}
          <button type="submit" disabled={saving} data-testid="crm-rep-submit" className="w-full flex items-center justify-center gap-2 bg-[#0F172A] text-white py-2.5 text-sm font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add sales rep"}
          </button>
        </form>
        <div className="p-6">
          <h4 className="text-xs uppercase tracking-wide text-zinc-400 mb-3">Existing reps ({reps.length})</h4>
          {reps.length === 0 ? <p className="text-sm text-zinc-400">No sales reps yet.</p> : (
            <div className="space-y-2" data-testid="crm-reps-list">
              {reps.map((r) => (
                <RepRow key={r.id} rep={r} onChange={onChange} onCredential={onCredential} />
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function RepRow({ rep, onChange, onCredential }) {
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [name, setName] = useState(rep.name);
  const [email, setEmail] = useState(rep.email);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resetting, setResetting] = useState(false);

  const save = async () => {
    if (!name.trim() || !email.trim()) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      await api.patch(`/crm/reps/${rep.id}`, { name: name.trim(), email: email.trim() });
      toast.success("Sales rep updated");
      setEditing(false);
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      const { data } = await api.delete(`/crm/reps/${rep.id}`);
      const n = data?.unassigned_customers || 0;
      toast.success(`Sales rep deleted${n ? ` — ${n} customer${n === 1 ? "" : "s"} unassigned` : ""}`);
      setConfirmDelete(false);
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally { setDeleting(false); }
  };

  const resetPassword = async () => {
    setResetting(true);
    try {
      const { data } = await api.post(`/crm/reps/${rep.id}/reset-password`);
      toast.success("New password generated");
      onCredential({ email: data.email, password: data.password });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally { setResetting(false); }
  };

  if (confirmDelete) {
    return (
      <div className="border border-red-300 bg-red-50 px-3 py-3 space-y-2" data-testid={`crm-rep-confirm-delete-${rep.id}`}>
        <p className="text-xs text-[#0F172A]">Delete <span className="font-semibold">{rep.name}</span>? Their assigned customers will become Unassigned.</p>
        <div className="flex items-center gap-2">
          <button onClick={remove} disabled={deleting} data-testid={`crm-rep-confirm-delete-btn-${rep.id}`} className="flex-1 flex items-center justify-center gap-1.5 bg-red-600 text-white py-2 text-xs font-medium hover:bg-red-700 transition-colors disabled:opacity-60">
            {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Trash2 className="h-3.5 w-3.5" /> Delete</>}
          </button>
          <button onClick={() => setConfirmDelete(false)} data-testid={`crm-rep-cancel-delete-${rep.id}`} className="px-3 py-2 text-xs border border-zinc-300 text-zinc-500 hover:text-[#0F172A] transition-colors">Cancel</button>
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="border border-[#D4AF37]/50 bg-[#D4AF37]/5 px-3 py-3 space-y-2" data-testid={`crm-rep-edit-${rep.id}`}>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid={`crm-rep-edit-name-${rep.id}`} placeholder="Name" className="w-full px-2.5 py-2 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={`crm-rep-edit-email-${rep.id}`} placeholder="Email" className="w-full px-2.5 py-2 border border-zinc-300 text-sm font-mono outline-none focus:ring-2 focus:ring-[#D4AF37]" />
        <div className="flex items-center gap-2">
          <button onClick={save} disabled={saving} data-testid={`crm-rep-save-${rep.id}`} className="flex-1 flex items-center justify-center gap-1.5 bg-[#0F172A] text-white py-2 text-xs font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="h-3.5 w-3.5" /> Save</>}
          </button>
          <button onClick={() => { setEditing(false); setName(rep.name); setEmail(rep.email); }} data-testid={`crm-rep-cancel-${rep.id}`} className="px-3 py-2 text-xs border border-zinc-300 text-zinc-500 hover:text-[#0F172A] transition-colors">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between border border-zinc-200 px-3 py-2 text-sm">
      <div className="min-w-0">
        <span className="font-medium text-[#0F172A] block truncate">{rep.name}</span>
        <span className="text-xs text-zinc-400 font-mono block truncate">{rep.email}</span>
      </div>
      <div className="ml-3 shrink-0 flex items-center gap-3">
        <button onClick={resetPassword} disabled={resetting} data-testid={`crm-rep-reset-btn-${rep.id}`} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-[#D4AF37] transition-colors disabled:opacity-60">
          {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />} Reset PW
        </button>
        <button onClick={() => setEditing(true)} data-testid={`crm-rep-edit-btn-${rep.id}`} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-[#0F172A] transition-colors">
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
        <button onClick={() => setConfirmDelete(true)} data-testid={`crm-rep-delete-btn-${rep.id}`} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-red-600 transition-colors">
          <Trash2 className="h-3.5 w-3.5" /> Delete
        </button>
      </div>
    </div>
  );
}

function F({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#0F172A] mb-1.5">{label}</label>
      <input type={type} required={required} value={value} onChange={onChange} data-testid={testid} className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow" />
    </div>
  );
}

const IMPORT_TEMPLATE = "company_name,contact_name,email,phone,sales_person,address,city,state,zip_code,lat,lng\nAcme Co,Jane Doe,jane@acme.com,318-555-1000,Eric Fletcher,100 Main St,Shreveport,LA,71106,32.5,-93.75\n";

function ImportModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = () => {
    const blob = new Blob([IMPORT_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "peregrine-customers-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async () => {
    if (!file) { toast.error("Choose a CSV or Excel file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/crm/customers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.created} customer${data.created === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="crm-import-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-white border border-zinc-200 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between p-5 border-b border-zinc-200 bg-[#0F172A] text-white">
          <div className="flex items-center gap-2"><FileSpreadsheet className="h-4 w-4 text-[#D4AF37]" /><h3 className="font-display font-bold">Import Customers (CSV / Excel)</h3></div>
          <button onClick={onClose} data-testid="crm-close-import"><X className="h-5 w-5 text-zinc-300 hover:text-white" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-zinc-600">Upload a CSV or Excel (.xlsx) file with columns: <span className="font-mono text-xs">company_name, contact_name, email, phone, sales_person, address, city, state, zip_code, lat, lng</span>. Each imported customer also gets a Credit Portal login.</p>
          <button onClick={downloadTemplate} data-testid="crm-import-template" className="flex items-center gap-2 text-sm text-[#0F172A] underline underline-offset-2">
            <FileSpreadsheet className="h-4 w-4" /> Download CSV template
          </button>
          <input type="file" accept=".csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="crm-import-file"
            className="block w-full text-sm text-zinc-600 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#0F172A] file:text-white file:text-sm file:cursor-pointer border border-zinc-300 p-2" />
          <button onClick={upload} disabled={uploading} data-testid="crm-import-submit" className="w-full flex items-center justify-center gap-2 bg-[#0F172A] text-white py-3 font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4" /> Upload & import</>}
          </button>

          {result && (
            <div className="border border-zinc-200 p-4 space-y-2" data-testid="crm-import-result">
              <p className="text-sm font-medium text-[#10B981]">{result.created} customer(s) imported.</p>
              {result.skipped?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 mb-1">{result.skipped.length} row(s) skipped:</p>
                  <ul className="text-xs text-zinc-500 space-y-0.5 max-h-40 overflow-y-auto">
                    {result.skipped.map((s, i) => (
                      <li key={s.email || s.company || `row-${s.row}-${i}`}>Row {s.row}: {s.reason} {s.email ? `(${s.email})` : s.company ? `(${s.company})` : ""}</li>
                    ))}
                  </ul>
                </div>
              )}
              <button onClick={onDone} data-testid="crm-import-done" className="mt-2 w-full bg-[#0F172A] text-white py-2.5 text-sm font-medium hover:bg-[#1E293B] transition-colors">Done</button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function CredentialModal({ cred, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(`Peregrine Portal\nEmail: ${cred.email}\nPassword: ${cred.password}`);
    setCopied(true); toast.success("Credentials copied"); setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="crm-credential-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-white border border-zinc-200 w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200 bg-[#0F172A] text-white">
          <h3 className="font-display font-bold">Login credentials generated</h3>
          <button onClick={onClose} data-testid="crm-close-credential"><X className="h-5 w-5 text-zinc-300 hover:text-white" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-zinc-500">Share these. The password is shown once.</p>
          <div className="border border-zinc-200 divide-y divide-zinc-100 font-mono text-sm">
            <div className="flex justify-between px-4 py-3"><span className="text-xs uppercase text-zinc-400">Email</span><span className="text-zinc-700">{cred.email}</span></div>
            <div className="flex justify-between px-4 py-3"><span className="text-xs uppercase text-zinc-400">Password</span><span className="text-[#0F172A] font-medium">{cred.password}</span></div>
          </div>
          <button onClick={copy} data-testid="crm-copy-credentials" className="w-full flex items-center justify-center gap-2 bg-[#0F172A] text-white py-3 font-medium hover:bg-[#1E293B] transition-colors">
            {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy all</>}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
